import { POPULAR_SEARCH_LOCATIONS } from "@/components/Home/Hero/constants";
import { DropdownOption } from "@/components/Home/Hero/Search/interface";
import { searchProperties } from "@/hooks/useProperty";
import { useQueryClient } from "@tanstack/react-query";
import { uniqBy } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

export const usePropertySearch = (isInitialized: boolean) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");
  const [recentlySearchedLocations, setRecentlySearchedLocations] = useState<
    Array<DropdownOption>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const queryClient = useQueryClient();
  const t = useTranslations("shared.search");
  const locale = useLocale();

  const loadOptions = async (value: string) => {
    if (!value) {
      if (recentlySearchedLocations?.length > 0) {
        return {
          options: [
            {
              label: t("recently-searched"),
              options: recentlySearchedLocations,
            },
          ],
        };
      }

      return {
        options: [
          {
            label: t("popular-locations"),
            options: POPULAR_SEARCH_LOCATIONS,
          },
        ],
      };
    }

    setSearchText(value);
    setDebouncedSearchText(value);
    setIsLoading(true);

    const data = await queryClient.fetchInfiniteQuery({
      queryKey: ["PROPERTIES", "SEARCH", value],
      queryFn: () => searchProperties(value, locale),
      getNextPageParam: () => null,
      initialPageParam: value,
    });

    const options: Array<DropdownOption> = data.pages
      .flatMap((page) => page)
      .map((location) => ({
        ...location,
        label: location.name,
        location: location.pathName,
        centralPoint: location.centralPoint,
        slug: location.slug,
        fullSlug: location.fullSlug,
        value: location.fullSlug,
      }));

    setIsLoading(false);

    return {
      options,
      hasMore: false,
    };
  };

  const updateRecentlySearchedLocations = (option: DropdownOption) => {
    const updatedLocations = uniqBy(
      [...recentlySearchedLocations, option],
      "value"
    );

    setRecentlySearchedLocations(updatedLocations);
    localStorage.setItem(
      "SH_recently_searched_locations",
      JSON.stringify(updatedLocations)
    );
  };

  const handleInputChange = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      const storedLocations = localStorage.getItem(
        "SH_recently_searched_locations"
      );
      if (storedLocations) {
        setRecentlySearchedLocations(JSON.parse(storedLocations));
      }
    }
  }, [isInitialized]);

  return {
    menuOpen,
    setMenuOpen,
    searchText,
    debouncedSearchText,
    isLoading,
    recentlySearchedLocations,
    loadOptions,
    updateRecentlySearchedLocations,
    handleInputChange,
  };
};
